import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { playlistPath } from '../playlists/playlistRoutes';
import type { HomePlaylistCard as CardModel } from './buildHomePlaylistCards';
import { HomePlaylistPreview } from './HomePlaylistPreview';

type HomePlaylistCardProps = {
  card: CardModel;
  canManage: boolean;
};

export function HomePlaylistCard({ card, canManage }: HomePlaylistCardProps) {
  const { t } = useTranslation('home');
  return (
    <article
      className="flex flex-col gap-3 rounded-2xl border p-3.5 shadow-[var(--gs-shadow,0_1px_2px_rgba(20,20,20,0.04),0_6px_20px_rgba(20,20,20,0.05))]"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <Link to={playlistPath(card.id)} aria-label={t('playlistCard.openNamed', { name: card.name })} className="block">
        <HomePlaylistPreview previews={card.previews} className="h-[140px] rounded-[10px]" />
      </Link>

      <div className="flex items-start gap-2">
        <h3
          className="m-0 min-w-0 flex-1 truncate text-[17px] font-bold tracking-[-0.01em]"
          style={{ color: 'var(--gs-ink)' }}
        >
          {card.name}
        </h3>
        <Link
          to={playlistPath(card.id)}
          title={t('playlistCard.openPlaylist')}
          className="grid p-0.5"
          style={{ color: 'var(--gs-muted)' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </Link>
      </div>

      <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-[13px]" style={{ color: 'var(--gs-muted)' }}>
        <span className="inline-flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          {card.itemsLabel}
        </span>
        {card.durationLabel ? (
          <span className="inline-flex items-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {card.durationLabel}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M9 20h6M12 16v4" />
          </svg>
          {card.screensLabel}
        </span>
      </div>

      <div className="mt-auto flex min-h-9 items-center justify-between gap-2.5">
        {card.playing ? (
          <span
            className="inline-flex items-center gap-2 rounded-[9px] px-3 py-1.5 text-[13px] font-bold"
            style={{
              color: 'var(--gs-ok)',
              background: 'color-mix(in oklch, var(--gs-ok) 12%, transparent)',
            }}
          >
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: 'var(--gs-ok)', animation: 'gs-pulse 2.4s ease-in-out infinite' }}
              aria-hidden
            />
            {t('playlistCard.playingNow')}
          </span>
        ) : card.screenCount > 0 ? (
          <span
            className="inline-flex items-center gap-2 rounded-[9px] px-3 py-1.5 text-[13px] font-semibold"
            style={{ color: 'var(--gs-ink2)', background: 'var(--gs-panel2)' }}
          >
            {card.statusLabel}
          </span>
        ) : (
          <>
            <span
              className="inline-flex items-center gap-2 rounded-[9px] px-3 py-1.5 text-[13px] font-semibold"
              style={{ color: 'var(--gs-muted)', background: 'var(--gs-panel2)' }}
            >
              <span
                className="h-2.5 w-2.5 rounded-full opacity-60"
                style={{ background: 'var(--gs-muted)' }}
                aria-hidden
              />
              {t('playlistCard.notAssigned')}
            </span>
            {canManage ? (
              <Link
                to={playlistPath(card.id)}
                className="inline-flex items-center gap-1.5 rounded-[9px] border px-3 py-1.5 text-[13px] font-semibold transition hover:border-[var(--gs-accent)]"
                style={{ borderColor: 'var(--gs-line)', color: 'var(--gs-ink)' }}
              >
                {t('playlistCard.assignScreens')}
              </Link>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
}
